// export Interfaces TypeScript
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';

export interface Material {
    _id: string;
    code: string;
    name: string;
    total: number;
  }
  
  export interface UnitOfMeasure {
    _id: string;
    code: string;
    name: string;
  }
  
  export interface OrderDetail {
    position: number;
    material: Material;
    baseQuantity: number;
    baseUnitOfMeasure: UnitOfMeasure;
    pararellQuantity: number;
    pararellUnitOfMeasure: UnitOfMeasure;
    total: number;
    // Otras propiedades que puedan ser necesarias
  }
  
  export interface Order {
    _id: string;
    serial: string;
    soldTo: {
      _id: string;
      externalKeys: {
        code: string;
      };
      name: string;
    };
    shipTo: {
      _id: string;
      externalKeys: {
        code: string;
      };
      name: string;
    };
    detail: OrderDetail[];
  }
  
  export interface ClientOrder {
    orderCount: number;
    id: string;
    shipTo: {
      _id: string;
      externalKeys: {
        code: string;
      };
      name: string;
    };
    orders: Order[];
  }

  function mapApiResponseToClientOrder(response: any): ClientOrder {
    return {
      orderCount: response.orderCount,
      id: response.id,
      shipTo: {
        _id: response.shipTo._id,
        externalKeys: {
          code: response.shipTo.externalKeys.code
        },
        name: response.shipTo.name
      },
      orders: response.orders.map((o: any): Order => {
        return {
          _id: o._id,
          serial: o.serial,
          soldTo: {
            _id: o.soldTo._id,
            externalKeys: {
              code: o.soldTo.externalKeys.code
            },
            name: o.soldTo.name
          },
          shipTo: {
            _id: o.shipTo._id,
            externalKeys: {
              code: o.shipTo.externalKeys.code
            },
            name: o.shipTo.name
          },
          detail: o.detail.map((d: any): OrderDetail => {
            return {
              position: d.position,
              material: {
                _id: d.material._id,
                code: d.material.code,
                name: d.material.name,
                total: d.material.total
              },
              baseQuantity: d.baseQuantity,
              baseUnitOfMeasure: {
                _id: d.baseUnitOfMeasure._id,
                code: d.baseUnitOfMeasure.code,
                name: d.baseUnitOfMeasure.name
              },
              pararellQuantity: d.pararellQuantity,
              pararellUnitOfMeasure: {
                _id: d.pararellUnitOfMeasure._id,
                code: d.pararellUnitOfMeasure.code,
                name: d.pararellUnitOfMeasure.name
              },
              total: d.total
            };
          }) || [] // Asumiendo que detail puede ser undefined
        };
      })
    };
  }
  
  // export Interface para cada material agrupado
  export interface MaterialSummary {
    materialCode: string;
    materialName: string;
    totalBaseQuantity: number;
    baseUnitOfMeasureCode: string;
    baseUnitOfMeasureName: string;
    totalParallelQuantity: number;
    parallelUnitOfMeasureCode: string;
    parallelUnitOfMeasureName: string;
    materialsTotalPrice: number;
  }
  
  // export Interface para el resultado final con cliente e primer nivel
  export interface ClientMaterialSummary {
    id?: string;
    orderCount?: number;
    clientCode: string;
    clientName: string;
    materialsTotalPrice: number;
    materials: MaterialSummary[];
    shipTo?: {
      _id: string;
      externalKeys: {
        code: string;
      };
      name: string;
    };
    soldTo?: {
      _id: string;
      externalKeys: {
        code: string;
      };
      name: string;
    };
  }
  
  // export Interface auxiliar para el procesamiento interno
  export interface ClientMaterials {
    clientCode: string;
    clientName: string;
    materials: Map<string, MaterialSummary>;
  }

  
  // Función principal para agrupar todos los materiales por cliente
  export function groupMaterialsByClient(clientOrders: ClientOrder[]): ClientMaterialSummary[] {
    const clientMaterialsMap = new Map<string, ClientMaterials>();


    // Procesar cada cliente y sus órdenes
    clientOrders.forEach(clientOrder => {
      clientOrder.orders.forEach(order => {
        const clientCode = order.shipTo.externalKeys.code;
        const clientName = order.shipTo.name;
        const clientKey = `${clientCode}-${clientName}`;
        
        // Inicializar cliente si no existe
        if (!clientMaterialsMap.has(clientKey)) {
          clientMaterialsMap.set(clientKey, {
            clientCode,
            clientName,
            materials: new Map()
          });
        }
        
        const clientMaterials = clientMaterialsMap.get(clientKey)!;
        
        // Procesar cada detalle de la orden
        order.detail.forEach(detail => {
          const materialKey = `${detail.material.code}-${detail.material.name}`;
          
          if (clientMaterials.materials.has(materialKey)) {
            // Sumar cantidades si el material ya existe
            const existingMaterial = clientMaterials.materials.get(materialKey)!;
            existingMaterial.totalBaseQuantity += detail.baseQuantity;
            existingMaterial.totalParallelQuantity += detail.pararellQuantity;
            existingMaterial.materialsTotalPrice += detail.material.total;
          } else {
            // Agregar nuevo material
            clientMaterials.materials.set(materialKey, {
              materialCode: detail.material.code,
              materialName: detail.material.name,
              totalBaseQuantity: detail.baseQuantity,
              totalParallelQuantity: detail.pararellQuantity,
              baseUnitOfMeasureCode: detail.baseUnitOfMeasure.code,
              baseUnitOfMeasureName: detail.baseUnitOfMeasure.name,
              parallelUnitOfMeasureCode: detail.pararellUnitOfMeasure.code,
              parallelUnitOfMeasureName: detail.pararellUnitOfMeasure.name,
              materialsTotalPrice: detail.material.total ?? 0
            });
          }
        });
      });
    });
    
    // Convertir el Map a array con la estructura final
    const result: ClientMaterialSummary[] = [];
    
    clientMaterialsMap.forEach(clientMaterials => {
      const materials: MaterialSummary[] = [];
      
      clientMaterials.materials.forEach(material => {
        materials.push(material);
      });
      
      result.push({
        clientCode: clientMaterials.clientCode,
        clientName: clientMaterials.clientName,
        materials: materials,
        materialsTotalPrice: materials.reduce((total, material) => total + material.materialsTotalPrice, 0)
      });
    });
    
    return result;
  }
  
  
  
  // Ejemplo de uso:
  /*
  // Supongamos que tienes un array de ClientOrder
  const clientOrdersList: ClientOrder[] = [
    // ... tu data aquí
  ];
  
  // Procesar y agrupar - NUEVA ESTRUCTURA
  const groupedMaterials = groupMaterialsByClient(clientOrdersList);
  
  // Resultado será algo como:
  // [
  //   {
  //     clientCode: "0001002257",
  //     clientName: "IRIS ISABEL VALLADARES CHEVEZ POLLOS ISABELA", 
  //     materials: [
  //       {
  //         materialCode: "000000000021000002",
  //         materialName: "PARTES DE POLLO CONG.",
  //         totalBaseQuantity: 50.15,
  //         baseUnitOfMeasure: "LB",
  //         totalParallelQuantity: 50,
  //         parallelUnitOfMeasure: "UN"
  //       },
  //       {
  //         materialCode: "000000000021000009", 
  //         materialName: "BAND.S DE ALAS DE POLLO FRESCO",
  //         totalBaseQuantity: 10.12,
  //         baseUnitOfMeasure: "LB", 
  //         totalParallelQuantity: 5,
  //         parallelUnitOfMeasure: "UN"
  //       }
  //     ]
  //   }
  // ]
  
  // Para exportar (convierte a formato plano)
  const exportData = convertToExportFormat(groupedMaterials);
  
  // Asignar a variable del componente
  this.clientMaterialsSummary = groupedMaterials;
  this.exportableData = exportData;
  */

// New interfaces for grouped material API response (materials grouped with customers underneath)

export interface MaterialData {
  _id: string;
  code: string;
  externalKeys: {
    code: string;
    _id: string;
  };
  name: string;
  UMPActive: boolean;
  batchControlActive: boolean;
  dimensions: {
    height: number;
    width: number;
    length: number;
    grossWeight: number;
    netWeight: number;
    weightUnitOfMeasureCode: string;
    volume: number;
    volumeUnitOfMeasureCode: string;
  };
  materialGroups: any[];
  materialImages: any[];
}

export interface BusinessPartnerOrder {
  serial: string;
  shipTo: {
    _id: string;
    id: number;
    externalKeys: {
      code: string;
      _id: string;
    };
    name: string;
    types: Array<{
      _id: string;
      code: string;
      name: string;
      isEmployee: boolean;
      allowUserCreation: boolean;
      statusId: StatusEnum;
    }>;
    ownPriceList: {
      _id: string;
      code: string;
      name: string;
      statusId: StatusEnum;
    };
  };
  soldTo: {
    _id: string;
    id: number;
    externalKeys: {
      code: string;
      _id: string;
    };
    name: string;
    types: Array<{
      _id: string;
      code: string;
      name: string;
      isEmployee: boolean;
      allowUserCreation: boolean;
      statusId: StatusEnum;
    }>;
    ownPriceList: {
      _id: string;
      code: string;
      name: string;
      statusId: StatusEnum;
    };
  };
  _id: string;
  detail: any;
}

export interface BusinessPartnerWithOrders {
  shipToMongoId: string;
  shipToName: string;
  shipToExternalKeys: string;
  ordersCount: number;
  baseQuantity: number;
  pararellQuantity: number;
  soldQuantity: number;
  orders: BusinessPartnerOrder[];
  // ✨ Propiedades agregadas en runtime (enriquecimiento de datos)
  // Copiadas del material padre para que el cell renderer genérico funcione
  baseUnitOfMeasure?: {
    _id: string;
    code: string;
    name: string;
  };
  pararellUnitOfMeasure?: {
    _id: string;
    code: string;
    name: string;
  };
}

export interface GroupedMaterialWithCustomers {
  materialId: string;
  code: string;
  name: string;
  materialCode: string;
  materialName: string;
  totalBaseQuantity: number;
  totalParallelQuantity: number;
  totalMaterialsQuantity: number;
  materialsTotalPrice: number;
  totalPararellQuantity: number;
  totalOrderCount: number;
  totalSoldQuantity: number;
  materialData: MaterialData;
  baseUnitOfMeasure: {
    _id: string;
    code: string;
    name: string;
  };
  pararellUnitOfMeasure: {
    _id: string;
    code: string;
    name: string;
  };
  businessPartners: BusinessPartnerWithOrders[];
}

export interface GroupedMaterialApiResponse {
  status: string;
  statusCode: string;
  data: {
    data: GroupedMaterialWithCustomers[];
    pageInfo: Array<{
      total: number;
      page: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    }>;
  };
}